// Rule-based комментарии к ходу (без DOM, без LLM). commentFor строит {key,params}
// по лестнице приоритетов: допустил мат → упустил мат → повесил фигуру →
// упустил выигрыш → зевок/ошибка/неточность → похвала → книга. Первое совпадение
// выигрывает. SAN и клетки НЕ переводятся; {piece} — тип фигуры (буква), который
// вызывающий локализует через t('cmt.piece.<type>'). Покрыт vitest на каждую ступень.
import { Chess } from 'chess.js';
import type { GameNode } from '@ext/core/types';
import type { MoveQuality } from '@ext/core/classify';
import { engineArtifactsValid, moverMate, nodeEvalKey, nodeFen, nodeMateWhite, winPct } from '@ext/core/evals';
import { uciToSan } from '@ext/core/eval-utils';
import { findHangingPieces } from '@ext/core/hanging';
import type { EvalStore } from '@ext/core/evals';
import { pvFacts } from './pv-facts';

export interface CommentCtx {
  evals: EvalStore;
  /** Целевая глубина артефактов движка (валидность _bestmove/_mate). */
  depth: number;
  /** Готовая классификация этого хода (из review-model). */
  quality: MoveQuality | null;
  /** «ECO Название» по FEN или '' — включает ступень «книга» (опционально). */
  openingName?: (fen: string) => string;
}

export interface Comment {
  key: string;
  params?: Record<string, string | number>;
  /** Дополнительный тактический факт из PV (лестница выше основного вердикта). */
  fact?: Comment;
}

/** Мат считаем «известным по дистанции», только если это реальный артефакт (не ±99 из cp). */
const MATE_UNKNOWN = 99;

/** SAN лучшего хода из позиции ПЕРЕД ходом (или '' если движок ещё не посчитал). */
function bestSan(node: GameNode, depth: number): string {
  const p = node.parent;
  if (!p || !p._bestmove || !engineArtifactsValid(p, depth)) return '';
  try {
    return uciToSan(nodeFen(p), p._bestmove);
  } catch {
    return '';
  }
}

/** win% со стороны ходившего (moverWhite) из cp-со-стороны-белых. */
function moverWin(cpWhite: number | null, moverWhite: boolean): number | null {
  if (cpWhite == null) return null;
  const w = winPct(cpWhite);
  return moverWhite ? w : 100 - w;
}

/** Потеря win% (свинг) ходившего, округлённая, не отрицательная. */
function swingOf(node: GameNode, evals: EvalStore): number {
  const p = node.parent!;
  const before = moverWin(evals.get(nodeEvalKey(p)), node.moverWhite === true);
  const after = moverWin(evals.get(nodeEvalKey(node)), node.moverWhite === true);
  if (before == null || after == null) return 0;
  return Math.max(0, Math.round(before - after));
}

/** Самая ценная висящая фигура ходившего на результирующей позиции (или null). */
function hungPiece(node: GameNode): { type: string; square: string } | null {
  try {
    const chess = new Chess();
    chess.load(nodeFen(node), { skipValidation: true });
    const mover = node.moverWhite ? 'w' : 'b';
    const hanging = findHangingPieces(chess, mover);
    if (!hanging.length) return null;
    const top = hanging.reduce((a, b) => (b.value > a.value ? b : a));
    return { type: top.type, square: top.square };
  } catch {
    return null;
  }
}

/** Выбрать ключ с/без «лучший ход {best}»: пустой best → суффикс _plain. */
function withBest(base: string, params: Record<string, string | number>, best: string): Comment {
  return best ? { key: base, params: { ...params, best } } : { key: `${base}_plain`, params };
}

/** Качества, к которым имеет смысл добавлять тактический факт из PV. */
const FACT_QUALITIES = new Set<MoveQuality>(['blunder', 'mistake', 'miss']);
/** Порог «выигрыш/потеря материала» в пешках (размен качества = 2). */
const MATERIAL_TH = 2;

/** Валидная лучшая линия из позиции ПЕРЕД ходом (что надо было играть). */
function bestLine(node: GameNode, depth: number): { fen: string; pv: string[] } | null {
  const p = node.parent;
  if (!p || !engineArtifactsValid(p, depth) || !p._pv?.length) return null;
  return { fen: nodeFen(p), pv: p._pv };
}

/** Опровержение сыгранного хода (лучшая линия соперника из результирующей позиции). */
function refutation(node: GameNode, depth: number): { fen: string; pv: string[] } | null {
  if (!engineArtifactsValid(node, depth) || !node._pv?.length) return null;
  return { fen: nodeFen(node), pv: node._pv };
}

/** Один надёжный тактический факт: мат/вилка/выигрыш из лучшей линии, иначе — потеря
 *  материала из опровержения. При сомнении — null (молчим). */
function tacticalFact(node: GameNode, ctx: CommentCtx, best: string): Comment | null {
  const { depth, quality } = ctx;
  if (!quality || !FACT_QUALITIES.has(quality)) return null;

  const bl = bestLine(node, depth);
  if (bl) {
    const f = pvFacts(bl.fen, bl.pv);
    const line = f.firstMoves.join(' ');
    if (f.givesMateIn != null && line) {
      return { key: f.backRank ? 'cmt.pv_backrank' : 'cmt.pv_mate', params: { n: f.givesMateIn, line } };
    }
    if (f.fork && best) return { key: 'cmt.pv_fork', params: { best, sq1: f.fork.sq1, sq2: f.fork.sq2 } };
    if (f.materialDelta >= MATERIAL_TH && f.capturedPiece && best) {
      return { key: 'cmt.pv_wins', params: { best, piece: f.capturedPiece } };
    }
  }

  const ref = refutation(node, depth);
  if (ref) {
    const f = pvFacts(ref.fen, ref.pv);
    const line = f.firstMoves.join(' ');
    if (f.materialDelta >= MATERIAL_TH && f.capturedPiece && line) {
      return { key: 'cmt.pv_loss', params: { line, piece: f.capturedPiece } };
    }
  }
  return null;
}

/**
 * Комментарий к ходу `node` (уже сыгранному). Возвращает {key, params, fact?} для t(),
 * либо null (тогда вызывающий рисует запасной вид SAN+качество+оценка). Основной
 * вердикт строит лестница приоритетов; `fact` добавляет тактику из PV поверх него.
 */
export function commentFor(node: GameNode, ctx: CommentCtx): Comment | null {
  const best = node?.parent && node.san ? bestSan(node, ctx.depth) : '';
  const verdict = verdictFor(node, ctx, best);
  if (!verdict) return null;
  const fact = tacticalFact(node, ctx, best);
  return fact ? { ...verdict, fact } : verdict;
}

/** Основной вердикт по лестнице приоритетов (без тактических фактов). */
function verdictFor(node: GameNode, ctx: CommentCtx, best: string): Comment | null {
  if (!node?.parent || !node.san) return null;
  const { evals, depth, quality } = ctx;

  const beforeMate = moverMate(node, nodeMateWhite(node.parent, evals, depth));
  const afterMate = moverMate(node, nodeMateWhite(node, evals, depth));

  // 1. Допустил мат: после хода у соперника форсированный мат (и раньше его не было).
  if (afterMate != null && afterMate < 0 && !(beforeMate != null && beforeMate < 0)) {
    const n = Math.abs(afterMate);
    if (n < MATE_UNKNOWN) return withBest('cmt.allowed_mate', { n }, best);
  }

  // 2. Упустил мат: до хода мат был за нас, после — нет.
  if (beforeMate != null && beforeMate > 0 && beforeMate < MATE_UNKNOWN && !(afterMate != null && afterMate > 0)) {
    return withBest('cmt.missed_mate', { n: beforeMate }, best);
  }

  // 3. Повесил/оставил фигуру под боем — уточнение к зевку/ошибке.
  if (quality === 'blunder' || quality === 'mistake') {
    const hung = hungPiece(node);
    if (hung) return withBest('cmt.hung_piece', { piece: hung.type, square: hung.square }, best);
  }

  // 4. Упустил выигрыш.
  if (quality === 'miss') return withBest('cmt.miss', {}, best);

  // 5. Зевок / ошибка / неточность с win%-свингом.
  if (quality === 'blunder') return withBest('cmt.blunder', { swing: swingOf(node, evals) }, best);
  if (quality === 'mistake') return withBest('cmt.mistake', { swing: swingOf(node, evals) }, best);
  if (quality === 'inaccuracy') return withBest('cmt.inaccuracy', { swing: swingOf(node, evals) }, best);

  // 6. Похвала.
  if (quality === 'brilliant') return { key: 'cmt.brilliant' };
  if (quality === 'great') return { key: 'cmt.great' };
  if (quality === 'best') return { key: 'cmt.best' };

  // 7. Книга (если подключён словарь дебютов).
  if (quality === 'book' && ctx.openingName) {
    const name = ctx.openingName(nodeFen(node));
    if (name) return { key: 'cmt.book', params: { name } };
  }

  return null;
}
